import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AsyncSection } from '@/app/components/AsyncSection';
import { SubmitError } from '@/app/components/SubmitError';
import { Button } from '@/app/components/ui/Button';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { FormActions } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useActiveCompany } from '@/features/company/CompanyContext';
import { useMe } from '@/features/me/MeContext';
import { getCompanySettings, updateCompanySettings } from '@/features/settings/api';
import { Toggle } from '@/features/settings/Toggle';
import { useAsync } from '@/lib/useAsync';
import { useSubmit } from '@/lib/useSubmit';

// Mirrors MIN_GROUP_SIZE_FLOOR in the API repo's `_constants_example.php`.
const MIN_GROUP_SIZE_FLOOR = 3;
const MIN_GROUP_SIZE_CEILING = 25;

/**
 * `/settings/privacy` — Instellingen › Privacy (§ WP-08): minimale
 * groepsgrootte/k-anonimiteit as a slider — this is the setting WP-02/03/06
 * read as `effective_min_group_size` (they start with a hardcoded default
 * of `DEFAULT_MIN_GROUP_SIZE` until they land; this WP only makes it
 * instelbaar, no blocking dependency the other way) — plus the four
 * toggles. Admin-only write (`companies.settings.update`), same as
 * Huisstijl.
 */
export function SettingsPrivacyPage() {
  const { t } = useTranslation();
  const { company } = useActiveCompany();
  const { permissions } = useMe();
  const companyId = company ? company.id : null;

  const state = useAsync(
    () => (companyId ? getCompanySettings(companyId) : Promise.reject(new Error('no company'))),
    [companyId],
  );

  const [minGroupSize, setMinGroupSize] = useState<number | null | undefined>(undefined);
  const [aggregated, setAggregated] = useState<boolean | undefined>(undefined);
  const [explicitConsent, setExplicitConsent] = useState<boolean | undefined>(undefined);
  const [dataExport, setDataExport] = useState<boolean | undefined>(undefined);

  const form = useSubmit(async () => {
    if (!companyId || !state.data) return;
    await updateCompanySettings(companyId, {
      min_group_size: minGroupSize !== undefined ? minGroupSize : state.data.min_group_size,
      logo_url: state.data.logo_url ?? null,
      accent_color: state.data.accent_color ?? null,
      privacy_aggregated_data: aggregated ?? state.data.privacy_aggregated_data ?? true,
      privacy_explicit_consent: explicitConsent ?? state.data.privacy_explicit_consent ?? false,
      privacy_data_export: dataExport ?? state.data.privacy_data_export ?? true,
      privacy_audit_log: true,
    });
    state.reload();
  });

  const canEdit = permissions.canUpdateCompanySettings;

  return (
    <section className="flex flex-col gap-6">
      <PageHeader title={t('settings.privacy.title')} lead={t('settings.privacy.lead')} />

      {!companyId && <p className="text-accent text-sm">{t('settings.noCompany')}</p>}
      {companyId && (
        <Card>
          <CardHeader title={t('settings.privacy.title')} />
          <AsyncSection state={state} label={t('settings.privacy.title')} skeleton="card">
            {(data) => {
              const currentMin =
                minGroupSize !== undefined
                  ? (minGroupSize ?? MIN_GROUP_SIZE_FLOOR)
                  : (data.min_group_size ?? data.effective_min_group_size);
              return (
                <form
                  className="flex flex-col gap-6"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void form.submit();
                  }}
                >
                  <div className="flex flex-col gap-2">
                    <label htmlFor="min-group-size" className="text-ink text-sm font-medium">
                      {t('settings.privacy.minGroupSize')}
                    </label>
                    <p className="text-accent text-[13px]">
                      {t('settings.privacy.minGroupSizeHint')}
                    </p>
                    <input
                      id="min-group-size"
                      type="range"
                      min={MIN_GROUP_SIZE_FLOOR}
                      max={MIN_GROUP_SIZE_CEILING}
                      step={1}
                      value={currentMin}
                      disabled={!canEdit}
                      onChange={(e) => setMinGroupSize(Number(e.target.value))}
                      className="accent-mint w-full"
                    />
                    <div className="text-ink text-sm font-semibold">
                      {t('settings.privacy.minGroupSizeValue', { count: currentMin })}
                    </div>
                    <div className="text-accent text-[13px]">
                      {t('settings.privacy.effective', { count: data.effective_min_group_size })}
                    </div>
                  </div>

                  <div className="border-line divide-line flex flex-col divide-y border-t">
                    <Toggle
                      label={t('settings.privacy.aggregatedData')}
                      hint={t('settings.privacy.aggregatedDataHint')}
                      checked={aggregated ?? data.privacy_aggregated_data ?? true}
                      onChange={setAggregated}
                      disabled={!canEdit}
                    />
                    <Toggle
                      label={t('settings.privacy.explicitConsent')}
                      hint={t('settings.privacy.explicitConsentHint')}
                      checked={explicitConsent ?? data.privacy_explicit_consent ?? false}
                      onChange={setExplicitConsent}
                      disabled={!canEdit}
                    />
                    <Toggle
                      label={t('settings.privacy.dataExport')}
                      hint={t('settings.privacy.dataExportHint')}
                      checked={dataExport ?? data.privacy_data_export ?? true}
                      onChange={setDataExport}
                      disabled={!canEdit}
                    />
                  </div>

                  <p className="text-accent text-[13px]">{t('settings.privacy.auditLogHint')}</p>

                  {!canEdit && <p className="text-accent text-[13px]">{t('settings.adminOnly')}</p>}
                  {canEdit && (
                    <>
                      <SubmitError error={form.error} />
                      <FormActions>
                        <Button type="submit" disabled={form.pending}>
                          {t('settings.save')}
                        </Button>
                      </FormActions>
                    </>
                  )}
                </form>
              );
            }}
          </AsyncSection>
        </Card>
      )}
    </section>
  );
}
